import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HiArrowTrendingUp,
  HiArrowTrendingDown,
  HiScale,
  HiArrowPath,
  HiBolt,
  HiArrowsRightLeft,
  HiPlusCircle,
  HiMoon,
  HiSparkles,
  HiQuestionMarkCircle,
  HiChatBubbleLeftEllipsis,
  HiCheckCircle,
  HiShieldCheck,
  HiPencilSquare
} from 'react-icons/hi2';
import { API_BASE } from '../../config';
import CoachChangeList from './CoachChangeList';
import { buildPreviewFromChanges } from '../../utils/plan_changes';

export const OPTION_KIND_META = {
  progress: { Icon: HiArrowTrendingUp, cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  maintain: { Icon: HiScale, cls: 'bg-gray-50 text-gray-700 border-gray-200' },
  swap_type: { Icon: HiArrowPath, cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  cross_train: { Icon: HiBolt, cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  move: { Icon: HiArrowsRightLeft, cls: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
  add_session: { Icon: HiPlusCircle, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  reduce: { Icon: HiArrowTrendingDown, cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  recovery: { Icon: HiMoon, cls: 'bg-teal-50 text-teal-700 border-teal-200' }
};

const STATE_CLS = {
  fresh: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  normal: 'bg-gray-50 text-gray-600 border-gray-200',
  tired: 'bg-amber-50 text-amber-800 border-amber-200',
  niggle: 'bg-orange-50 text-orange-800 border-orange-200',
  injured: 'bg-red-50 text-red-700 border-red-200'
};

export function OptionKindBadge({ kind }) {
  const { t } = useTranslation();
  const meta = OPTION_KIND_META[kind] || OPTION_KIND_META.maintain;
  const { Icon } = meta;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-black uppercase tracking-wide ${meta.cls}`}>
      <Icon className="w-3.5 h-3.5" />
      {t(`coach_options.kind.${kind}`, { defaultValue: String(kind || '').replace(/_/g, ' ') })}
    </span>
  );
}

async function postJson(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body)
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || 'Request failed');
  return data;
}

/**
 * Coach v2 proposal: the coach's read, an optional question for the athlete and 0-2 options
 * (with the coach's pick). Applying/dismissing goes through the generic coach-options endpoints.
 */
export default function CoachOptions({
  analysis,
  decision = null,
  planId,
  source,
  sourceId,
  canEdit,
  onApplied,
  onAnswerQuestion = null,
  plan = null,
  showReadOnlyNotice = true,
  className = ''
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(null); // option id | '__keep__'
  const [error, setError] = useState('');
  const [localDecision, setLocalDecision] = useState(null);

  if (!analysis) return null;

  const options = Array.isArray(analysis.options) ? analysis.options.filter(Boolean) : [];
  const pickId = analysis?.coach_pick?.option_id ?? null;
  const pickReason = analysis?.coach_pick?.reason || '';
  const effectiveDecision = localDecision || decision || analysis.decision || null;
  const decided = effectiveDecision?.action === 'applied' || effectiveDecision?.action === 'kept_plan';
  const appliedOption = effectiveDecision?.action === 'applied'
    ? options.find((o) => String(o.id) === String(effectiveDecision.option_id)) || null
    : null;
  const showCtas = Boolean(canEdit && !decided && planId && sourceId);
  const athleteState = analysis.athlete_state && !['unknown', 'normal'].includes(analysis.athlete_state) ? analysis.athlete_state : null;

  const handleApply = async (option) => {
    if (!showCtas || busy) return;
    setBusy(String(option.id));
    setError('');
    try {
      await postJson(`${API_BASE}/plans/${planId}/coach-options/apply`, {
        source,
        source_id: sourceId,
        option_id: option.id
      });
      setLocalDecision({ action: 'applied', option_id: option.id, kind: option.kind, at: new Date().toISOString() });
      onApplied?.({ action: 'applied', option });
    } catch (e) {
      setError(e?.message || t('coach_options.error'));
    } finally {
      setBusy(null);
    }
  };

  const handleKeep = async () => {
    if (!showCtas || busy) return;
    setBusy('__keep__');
    setError('');
    try {
      await postJson(`${API_BASE}/plans/${planId}/coach-options/dismiss`, { source, source_id: sourceId });
      setLocalDecision({ action: 'kept_plan', at: new Date().toISOString() });
      onApplied?.({ action: 'kept_plan' });
    } catch (e) {
      setError(e?.message || t('coach_options.error'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={`space-y-5 ${className}`}>
      {/* Coach read */}
      {analysis.coach_read ? (
        <div className="relative rounded-3xl border border-indigo-100 bg-gradient-to-br from-white to-indigo-50/60 p-5 md:p-6 shadow-sm">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-indigo-700">
              <HiChatBubbleLeftEllipsis className="w-5 h-5" />
              {t('coach_options.coach_read_title')}
            </div>
            {athleteState ? (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-black uppercase tracking-wide ${STATE_CLS[athleteState] || STATE_CLS.normal}`}>
                {t(`coach_options.athlete_state.${athleteState}`, { defaultValue: athleteState })}
              </span>
            ) : null}
          </div>
          <p className="text-gray-800 text-base md:text-lg leading-relaxed font-serif whitespace-pre-line">
            {analysis.coach_read}
          </p>
        </div>
      ) : null}

      {/* Question for the athlete */}
      {analysis.question_for_athlete ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 md:p-5 flex items-start gap-3">
          <HiQuestionMarkCircle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-black uppercase tracking-widest text-amber-700 mb-1">
              {t('coach_options.question_title')}
            </div>
            <div className="text-sm md:text-base font-bold text-amber-950 leading-relaxed">
              {analysis.question_for_athlete}
            </div>
            {source === 'workout' && canEdit ? (
              <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="text-xs text-amber-800 font-medium">{t('coach_options.question_hint')}</div>
                {typeof onAnswerQuestion === 'function' ? (
                  <button
                    type="button"
                    onClick={onAnswerQuestion}
                    className="shrink-0 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-600 text-white text-xs font-black hover:bg-amber-700 transition-colors"
                  >
                    <HiPencilSquare className="w-4 h-4" /> {t('coach_options.answer_cta')}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Decision banner */}
      {decided ? (
        effectiveDecision.action === 'applied' ? (
          <div className="rounded-2xl border border-green-200 bg-green-50 p-4 flex items-start gap-3">
            <HiCheckCircle className="w-6 h-6 text-green-600 shrink-0" />
            <div>
              <div className="text-sm font-black text-green-900">{t('coach_options.decision_applied')}</div>
              {appliedOption?.label ? <div className="text-sm text-green-800 mt-0.5">{appliedOption.label}</div> : null}
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 flex items-start gap-3">
            <HiShieldCheck className="w-6 h-6 text-gray-500 shrink-0" />
            <div className="text-sm font-black text-gray-700">{t('coach_options.decision_kept')}</div>
          </div>
        )
      ) : null}

      {/* No option: the coach keeps the plan */}
      {options.length === 0 ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 flex items-start gap-3">
          <HiShieldCheck className="w-6 h-6 text-emerald-600 shrink-0" />
          <div>
            <div className="text-base font-black text-emerald-900">{t('coach_options.keep_plan_title')}</div>
            {pickReason ? <div className="text-sm text-emerald-800 mt-1 leading-relaxed">{pickReason}</div> : null}
          </div>
        </div>
      ) : (
        <>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-1.5 h-6 bg-indigo-500 rounded-full" />
              <h4 className="text-lg md:text-xl font-black text-gray-900 tracking-tight">
                {t('coach_options.options_title', { count: options.length })}
              </h4>
            </div>
            {!decided ? <p className="text-sm text-gray-500 ml-4">{t('coach_options.options_intro')}</p> : null}
          </div>

          <div className={`grid gap-4 ${options.length > 1 ? 'md:grid-cols-2' : ''}`}>
            {options.map((option) => {
              const isPick = pickId != null && String(pickId) === String(option.id);
              const isApplied = appliedOption && String(appliedOption.id) === String(option.id);
              const preview = Array.isArray(option.preview) && option.preview.length > 0
                ? option.preview
                : buildPreviewFromChanges(option.changes, plan);
              const dimmed = decided && !isApplied;
              return (
                <div
                  key={option.id}
                  className={[
                    'relative rounded-3xl border bg-white p-5 flex flex-col shadow-sm transition-all',
                    isApplied ? 'border-green-400 ring-2 ring-green-200' : isPick ? 'border-indigo-300 ring-2 ring-indigo-100' : 'border-gray-200',
                    dimmed ? 'opacity-60' : ''
                  ].join(' ')}
                >
                  {isPick ? (
                    <div className="absolute -top-3 right-4 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-indigo-600 text-white text-[10px] font-black uppercase tracking-wide shadow-md">
                      <HiSparkles className="w-3.5 h-3.5" /> {t('coach_options.coach_pick')}
                    </div>
                  ) : null}

                  <div className="flex items-center gap-2 mb-2">
                    <OptionKindBadge kind={option.kind} />
                    {isApplied ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-800 border border-green-200 text-[10px] font-black uppercase">
                        <HiCheckCircle className="w-3.5 h-3.5" /> {t('coach_options.applied_badge')}
                      </span>
                    ) : null}
                  </div>

                  <div className="text-base md:text-lg font-black text-gray-900 leading-snug">{option.label}</div>
                  {option.rationale ? (
                    <p className="mt-2 text-sm text-gray-700 leading-relaxed">{option.rationale}</p>
                  ) : null}
                  {option.tradeoff ? (
                    <p className="mt-2 text-xs text-gray-500 leading-relaxed">
                      <span className="font-black uppercase tracking-wide text-gray-400 mr-1">{t('coach_options.tradeoff')}</span>
                      {option.tradeoff}
                    </p>
                  ) : null}

                  {isPick && pickReason ? (
                    <div className="mt-3 rounded-xl bg-indigo-50 border border-indigo-100 p-3 text-xs text-indigo-900 leading-relaxed">
                      <span className="font-black">{t('coach_options.why_pick')}</span> {pickReason}
                    </div>
                  ) : null}

                  {preview.length > 0 ? (
                    <div className="mt-4">
                      <div className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">
                        {t('coach_options.changes_title', { count: preview.length })}
                      </div>
                      <CoachChangeList preview={preview} />
                    </div>
                  ) : null}

                  {showCtas ? (
                    <button
                      type="button"
                      onClick={() => handleApply(option)}
                      disabled={Boolean(busy)}
                      className={[
                        'mt-5 w-full py-3 rounded-2xl font-black text-sm transition-all flex items-center justify-center gap-2',
                        busy
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : isPick
                            ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-500/30 hover:-translate-y-0.5'
                            : 'bg-white text-indigo-700 border-2 border-indigo-200 hover:border-indigo-300 hover:bg-indigo-50'
                      ].join(' ')}
                    >
                      {busy === String(option.id) ? (
                        <>
                          <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                          {t('coach_options.applying')}
                        </>
                      ) : (
                        t('coach_options.apply')
                      )}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>

          {/* Coach recommends keeping the plan although options exist */}
          {pickId == null && pickReason && !decided ? (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
              <HiSparkles className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-900 leading-relaxed">
                <span className="font-black">{t('coach_options.coach_pick_keep')}</span> {pickReason}
              </div>
            </div>
          ) : null}

          {showCtas ? (
            <button
              type="button"
              onClick={handleKeep}
              disabled={Boolean(busy)}
              className={[
                'w-full py-3 rounded-2xl font-bold text-sm transition-all border-2',
                pickId == null
                  ? 'border-emerald-300 text-emerald-800 bg-white hover:bg-emerald-50'
                  : 'border-gray-200 text-gray-600 bg-white hover:bg-gray-50 hover:border-gray-300',
                busy ? 'opacity-60 cursor-not-allowed' : ''
              ].join(' ')}
            >
              {busy === '__keep__' ? t('coach_options.saving') : t('coach_options.keep_plan')}
            </button>
          ) : null}

          {!canEdit && !decided && showReadOnlyNotice ? (
            <div className="w-full py-3 rounded-2xl font-bold bg-gray-50 text-gray-500 text-center border border-gray-200 text-sm">
              {t('workout.read_only')}
            </div>
          ) : null}
        </>
      )}

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-bold text-red-700">{error}</div>
      ) : null}
    </div>
  );
}
